/* WristBear: turns a numeric list filter (e.g. Case size) into a two-handle range slider.
   Progressive enhancement: the real checkboxes stay in the form (hidden). Moving the slider ticks
   every value whose number falls in the range, then lets Hyper's facet form re-render as usual.
   Labels like "38 MM", "38.4 MM", "Small (<36mm)" or "Medium (37mm – 41mm)" are all understood. */
if (!customElements.get('wb-value-range')) {
  class WbValueRange extends HTMLElement {
    static parse(label) {
      const nums = (label.match(/\d+(?:\.\d+)?/g) || []).map(Number);
      if (!nums.length) return null;
      if (nums.length >= 2) return [Math.min(nums[0], nums[1]), Math.max(nums[0], nums[1])];
      if (/<|under|below|up to/i.test(label)) return [-Infinity, nums[0]];
      if (/>|over|above|\+/i.test(label)) return [nums[0], Infinity];
      return [nums[0], nums[0]];
    }

    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.items = [...this.querySelectorAll('input[type="checkbox"]')]
        .map((box) => ({ box, r: WbValueRange.parse(box.dataset.label || box.value) }))
        .filter((x) => x.r);
      const usable = this.items.filter((x) => !x.box.disabled || x.box.checked);
      const ends = usable.flatMap((x) => x.r).filter(Number.isFinite);
      if (usable.length < 2 || ends.length < 2) return; // not numeric enough: keep the list

      this.min = Math.floor(Math.min(...ends));
      this.max = Math.ceil(Math.max(...ends));
      if (this.max <= this.min) return;
      this.step = 0.5;
      this.unit = this.dataset.unit || '';

      const checked = this.items.filter((x) => x.box.checked);
      let lo = this.min;
      let hi = this.max;
      if (checked.length) {
        // Rebuild the chosen range from exact sizes; bucket labels ("Medium (37-41)") only widen it
        // when nothing exact is selected. An end at the last exact size means "open" (slider end).
        const exact = (list) => list.filter((x) => x.r[0] === x.r[1]).map((x) => x.r[0]);
        const picked = exact(checked);
        const all = exact(usable);
        const ends2 = picked.length ? picked : checked.flatMap((x) => x.r).filter(Number.isFinite);
        lo = Math.min(...ends2) <= Math.min(...all) ? this.min : Math.max(this.min, Math.min(...ends2));
        hi = Math.max(...ends2) >= Math.max(...all) ? this.max : Math.min(this.max, Math.max(...ends2));
      }
      this.render(lo, hi);
      this.classList.add('is-enhanced');
    }

    fmt(v) {
      const n = Math.round(v * 10) / 10;
      return (Number.isInteger(n) ? n : n.toFixed(1)) + (this.unit ? ' ' + this.unit : '');
    }

    render(lo, hi) {
      const label = this.dataset.label || '';
      const wrap = document.createElement('div');
      wrap.className = 'wb-vrange__ui grid';
      wrap.innerHTML = `
        <div class="facet-range-wrapper relative">
          <input class="facet-range facet-range__min w-full pointer-events-none" type="range">
          <input class="facet-range facet-range__max w-full pointer-events-none absolute" type="range">
        </div>
        <div class="facet-input-wrapper wb-vrange__inputs flex items-center gap-4">
          <label class="flex items-center flex-grow relative gap-2"><span class="visually-hidden">From</span>
            <input class="form-control facet-range__input" type="number" inputmode="decimal"><span class="facet-range__symbol"></span></label>
          <label class="flex items-center flex-grow relative gap-2"><span class="visually-hidden">To</span>
            <input class="form-control facet-range__input" type="number" inputmode="decimal"><span class="facet-range__symbol"></span></label>
        </div>
        <p class="wb-vrange__out text-subtext" aria-live="polite"></p>`;
      this.prepend(wrap);
      this.track = wrap.querySelector('.facet-range-wrapper');
      this.out = wrap.querySelector('.wb-vrange__out');
      [this.lo, this.hi, this.loBox, this.hiBox] = wrap.querySelectorAll('input');
      // Stable ids so the facet form can put focus back after it re-renders the filters.
      const base = this.dataset.id || 'wb-vrange';
      [this.lo.id, this.hi.id, this.loBox.id, this.hiBox.id] = ['min', 'max', 'from', 'to'].map((k) => base + '-' + k);
      wrap.querySelectorAll('.facet-range__symbol').forEach((u) => (u.textContent = this.unit));
      for (const [box, range, name] of [[this.loBox, this.lo, 'Minimum'], [this.hiBox, this.hi, 'Maximum']]) {
        Object.assign(box, { min: this.min, max: this.max, step: this.step });
        box.setAttribute('aria-label', `${name} ${label.toLowerCase()}${this.unit ? ' (' + this.unit + ')' : ''}`);
        box.addEventListener('focus', () => box.select());
        box.addEventListener('input', (e) => e.stopPropagation());
        box.addEventListener('change', (e) => {
          const v = Math.min(this.max, Math.max(this.min, Number(box.value) || (range === this.lo ? this.min : this.max)));
          range.value = v;
          this.update(range);
          this.commit(e);
        });
      }
      for (const [input, value, name] of [[this.lo, lo, 'Minimum'], [this.hi, hi, 'Maximum']]) {
        Object.assign(input, { min: this.min, max: this.max, step: this.step, value });
        input.setAttribute('aria-label', `${name} ${label.toLowerCase()}`);
        input.addEventListener('input', () => this.update(input));
        // Pointer: commit on release (change). Keyboard: commit when the key is released or on blur,
        // not on every 0.5 mm step. PageUp/PageDown jump 2 mm.
        input.addEventListener('keydown', (e) => {
          this.keying = true;
          if (e.key === 'PageUp' || e.key === 'PageDown') {
            e.preventDefault();
            const delta = (e.key === 'PageUp' ? 1 : -1) * this.step * 4;
            input.value = Math.min(this.max, Math.max(this.min, Number(input.value) + delta));
            this.update(input);
          }
        });
        input.addEventListener('keyup', (e) => {
          if (/^(Arrow|Page|Home|End)/.test(e.key)) this.commit(e, 800);
        });
        input.addEventListener('blur', (e) => {
          this.keying = false;
          if (this.timer) this.commit(e, 0);
        });
        input.addEventListener('change', (e) => {
          if (this.keying) e.stopPropagation();
          else this.commit(e);
        });
        input.addEventListener('pointerdown', () => (this.keying = false));
      }
      this.update();
    }

    update(moved) {
      let lo = Number(this.lo.value);
      let hi = Number(this.hi.value);
      if (lo > hi) {
        if (moved === this.lo) this.lo.value = lo = hi;
        else this.hi.value = hi = lo;
      }
      const pct = (v) => ((v - this.min) / (this.max - this.min)) * 100 + '%';
      this.track.style.setProperty('--range-min', pct(lo));
      this.track.style.setProperty('--range-max', pct(hi));
      const n = this.inRange(lo, hi).length;
      this.out.textContent = n ? '' : 'No watches in this range';
      if (this.loBox && document.activeElement !== this.loBox) this.loBox.value = Math.round(lo * 10) / 10;
      if (this.hiBox && document.activeElement !== this.hiBox) this.hiBox.value = Math.round(hi * 10) / 10;
      this.lo.setAttribute('aria-valuetext', this.fmt(lo));
      this.hi.setAttribute('aria-valuetext', this.fmt(hi));
    }

    inRange(lo, hi) {
      return this.items.filter((x) => !x.box.disabled && x.r[0] <= hi && x.r[1] >= lo);
    }

    commit(event, delay = 350) {
      // Handle it here, then send one change to the facet form (debounced for keyboard use).
      event.stopPropagation();
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.timer = null;
        const lo = Number(this.lo.value);
        const hi = Number(this.hi.value);
        const full = lo <= this.min && hi >= this.max;
        const hits = this.inRange(lo, hi);
        if (!full && !hits.length) return;
        this.items.forEach((x) => { x.box.checked = !full && hits.includes(x); });
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }, delay);
    }
  }
  customElements.define('wb-value-range', WbValueRange);
}

/* WristBear: the drawer footer ("Show N watches") is re-rendered after every filter change, and the new
   button has no listener from the drawer (it binds its controls once). Close the drawer from a delegated
   listener instead; if the original listener already closed it, hide() is a no-op. */
document.addEventListener('click', function (event) {
  var btn = event.target.closest && event.target.closest('#DrawerFacetFooter [aria-controls="FacetDrawer"]');
  if (!btn) return;
  var drawer = document.getElementById('FacetDrawer');
  if (drawer && drawer.open && typeof drawer.hide === 'function') drawer.hide();
});
